import { Button, Checkbox, flash, FormGroup, Modal, SubmitButton, Table } from "@xola/ui-kit";
import { hasTimeScheduled, isAllDay, PurchaseItemStatus } from "common";
import { noop } from "lodash";
import pThrottle from "p-throttle";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Arrival,
    Event,
    EventGuide,
    Experience,
    ExperiencePurchaseItem,
    FeatureRole,
    Permission,
    Purchase,
    Timeslot,
} from "types";
import { api } from "../../api/api";
import { calculatePercentage } from "../../helpers/mathHelper";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { getCustomerName } from "../../models/Purchase";
import { getTimeslotIdFromPurchaseItem } from "../../models/PurchaseItem";
import { IsGranted } from "../Common/IsGranted";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { OverlayLoader } from "../Common/OverlayLoader";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ArrivalPicker } from "../DatePicker/ArrivalPicker";
import { Loader } from "../Loader";
import { updateTimeslotCache } from "./EditEventCapacityModal";
import { getBlackoutSchedulePayload } from "./EventSchedulesHelper";
import { EventSummaryTile } from "./EventSummaryTile";
import { useRosterDrawerStore } from "@/stores/rosterDrawerStore";

const throttle = pThrottle({
    limit: 1,
    interval: 1000,
});

interface EventChangeArrivalModalProps {
    timeslot: Timeslot;
    onClose: (e: any, redirectUrl?: string) => void;
    isOpen: boolean;
}

export const EventChangeArrivalModal = ({ timeslot, isOpen, onClose }: EventChangeArrivalModalProps) => {
    const [showCloseIcon, setShowCloseIcon] = useState(true);
    return (
        <Modal isOpen={isOpen} className="!p-4" onClose={showCloseIcon ? onClose : noop}>
            <ModalContent timeslot={timeslot} showCloseIcon={(value) => setShowCloseIcon(value)} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    timeslot: Timeslot;
    onClose: (e: any, redirectUrl?: string) => void;
    showCloseIcon: (value: boolean) => void;
}

const ModalContent = ({ timeslot, onClose, showCloseIcon }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const seller = useSeller();

    const { data: event, isLoading: isLoadingEvent } = useResource<Event>(["events", timeslot.event.id]);
    const { data: purchaseItems, isLoading: isLoadingPurchaseItems } = useInfiniteCollection<ExperiencePurchaseItem>(
        "purchaseItems",
        {
            event: event?.id,
        },
    );
    const filteredPurchaseItems = purchaseItems.filter((item) => item.status !== PurchaseItemStatus.CANCELED);

    const { data: experience } = useResource<Experience>(["products", event?.experience.id]);

    const isAllDayEvent = !!(experience && isAllDay(experience));

    const [isOverlayLoaderOpened, setIsOverlayLoaderOpened] = useState(false);
    const allowOverlap =
        seller.roles.includes(FeatureRole.SELLER_INVENTORY) && experience && experience.resources.length > 0;

    const [arrival, setArrival] = useState<Arrival>({
        date: "",
        time: null,
    });
    const { mutateAsync: blackoutEvent } = useMutation(async () => {
        const payload = getBlackoutSchedulePayload({ timeslot, event, experience });
        return api.post(`/experiences/${experience!.id}/schedules`, payload);
    });

    const { mutateAsync: unassignEventGuide } = useMutation(
        async ({ event, guide }: { event: Event; guide: EventGuide }) => {
            await api.delete(`/events/${event.id}/guides/${guide.guide.id}`);
        },
    );

    const history = useHistory();
    const isDashboardPage = useMemo(() => history.location.pathname.startsWith("/dashboard"), [history]);
    const isTheSameArrival = !arrival.date;

    const [successCount, setSuccessCount] = useState(0);
    const [selectedPurchases, setSelectedPurchases] = useState<ExperiencePurchaseItem[]>([]);
    const [showArrivalContent, setShowArrivalContent] = useState(true);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [notifyCustomer, setNotifyCustomer] = useState(true);
    const [blackoutTimeslot, setBlackoutTimeslot] = useState(false);
    const [unassignGuides, setUnassignGuides] = useState(false);
    const setDrawerClose = useRosterDrawerStore((state) => state.setClose);

    useEffect(() => {
        if (successCount === selectedPurchases.length) {
            showCloseIcon(true);
        }
    }, [successCount, selectedPurchases, showCloseIcon]);

    const showCancelButton = () => selectedPurchases.length > 1 && selectedPurchases.length - 1 > successCount;

    const closeModal = (e: any, redirectUrl?: string, updatedAll = false) => {
        setTimeout(() => {
            if (updatedAll || !showCancelButton()) {
                onClose(e);
            }

            if (redirectUrl) {
                onClose(e, redirectUrl);
            }
        }, 1000);
    };

    const throttledUpdate = throttle(
        async (purchaseItem: ExperiencePurchaseItem): Promise<[boolean, Purchase | null]> => {
            try {
                const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${purchaseItem.purchase.id}`, {
                    items: [
                        {
                            id: purchaseItem.id,
                            arrivalDate: purchaseItem.arrivalDate,
                            arrivalTime: isAllDayEvent ? null : purchaseItem.arrivalTime,
                        },
                    ],
                    _emailOverrides: { sendEmailToTraveler: notifyCustomer },
                });

                invalidate("purchases", purchaseItem?.purchase?.id);
                setSuccessCount((successCount) => successCount + 1);
                return [true, updatedPurchase];
            } catch {
                flash.danger("Error saving new time.");
                setSuccessCount((successCount) => successCount + 1);

                return [false, null];
            }
        },
    );

    const handleSubmit = async (e: any) => {
        let numbOfSuccess = 0;
        setShowArrivalContent(false);
        showCloseIcon(false);
        const { date, time } = arrival;

        const updatedDatePurchases = selectedPurchases.map((purchase: ExperiencePurchaseItem) => ({
            ...purchase,
            arrivalTime: time ?? purchase.arrivalTime,
            arrivalDate: date,
        }));

        setIsOverlayLoaderOpened(true);
        // Array to store the success status of each purchase item update
        const updatePromises = updatedDatePurchases.map(async (purchaseItem: ExperiencePurchaseItem) => {
            const [isSuccess, updatedPurchase] = await throttledUpdate(purchaseItem);
            return { isSuccess, updatedPurchase };
        });

        const updateResults = await Promise.all(updatePromises);

        const successResults = updateResults.filter((result) => result.isSuccess);
        numbOfSuccess = successResults.length;

        let updatedPurchaseItem: ExperiencePurchaseItem | undefined;

        const selectedPurchaseIds = new Set(selectedPurchases.map((item) => item.id));

        for (const result of successResults) {
            const updatedItems = result.updatedPurchase?.items;

            if (updatedItems) {
                for (const item of updatedItems) {
                    if (selectedPurchaseIds.has(item.id)) {
                        updatedPurchaseItem = item as ExperiencePurchaseItem;
                        break;
                    }
                }
            }

            if (updatedPurchaseItem) {
                break;
            }
        }

        if (numbOfSuccess === updatedDatePurchases.length && updatedPurchaseItem) {
            if (blackoutTimeslot) {
                await blackoutEvent();
            }

            const { data: updatedEvent } = await api.get<Event>(`/events/${updatedPurchaseItem.event.id}`);

            if (event && unassignGuides) {
                await Promise.all(
                    event.guides.map(async (guide) => {
                        await unassignEventGuide({ event, guide });
                    }),
                );
            }

            if (updatedEvent && isDashboardPage) {
                const productId = selectedPurchases[0].product.id;
                const oldArrivalDate = selectedPurchases[0].arrivalDate;
                const arrivalDate = updatedPurchaseItem?.arrivalDate;

                const timeslotParams1 = {
                    timeslot: null,
                    event: updatedEvent,
                    productId: productId,
                    startDate: arrivalDate,
                };

                const updateParams1 = {
                    isGranted: isGranted,
                    forceReplaceId: undefined,
                    shouldInsertToCache: true,
                };

                await updateTimeslotCache(timeslotParams1, updateParams1);

                const timeslotParams2 = {
                    timeslot: null,
                    event: null,
                    productId: productId,
                    startDate: oldArrivalDate,
                };

                const updateParams2 = {
                    isGranted: isGranted,
                    forceReplaceId: undefined,
                };

                await updateTimeslotCache(timeslotParams2, updateParams2);
            }

            const url =
                event?.purchaseItems.length === selectedPurchases.length
                    ? `/dashboard/${getTimeslotIdFromPurchaseItem(updatedPurchaseItem)}`
                    : undefined;

            setSelectedPurchases(updatedDatePurchases);
            setIsOverlayLoaderOpened(false);
            setDrawerClose();

            closeModal(e, url, true);
        } else {
            setIsOverlayLoaderOpened(false);
            onClose(e);
            showCloseIcon(true);
        }
    };

    const abortThrottle = (e: any) => {
        throttledUpdate.abort();
        setShowCancelModal(false);
        closeModal(e);
    };

    const allItemsSelected = selectedPurchases.length === filteredPurchaseItems.length;
    const isGuideRemovalSelectionDisabled = !allItemsSelected || isLoadingPurchaseItems;

    if (showArrivalContent) {
        return (
            <>
                <Modal.Header>Change Arrival</Modal.Header>
                <div className="mt-1 text-center text-gray-darker">
                    <span>Select groups to move to a new arrival</span>
                </div>
                <Modal.Body>
                    <fieldset disabled={isLoadingEvent}>{event ? <EventSummaryTile event={event} /> : null}</fieldset>
                    <FormGroup>
                        {event && (
                            <PurchaseList
                                filteredPurchaseItems={filteredPurchaseItems}
                                isLoading={isLoadingPurchaseItems}
                                onChange={(purchaseItem) => {
                                    setSelectedPurchases(purchaseItem);
                                }}
                            />
                        )}
                    </FormGroup>
                    <FormGroup>
                        {!isLoadingEvent && experience && (
                            <ArrivalPicker
                                showCapacity
                                autoSelectTimeslot={false}
                                product={event?.experience.id ?? ""}
                                showAddNewTimeSlot={hasTimeScheduled(experience)}
                                value={arrival}
                                allowOverlap={allowOverlap}
                                quantity={2}
                                onChange={({ date, time }: Arrival) => {
                                    setArrival({ date, time });
                                }}
                            />
                        )}
                    </FormGroup>
                    <FormGroup>
                        <Checkbox
                            checked={blackoutTimeslot}
                            label="Blackout old timeslot to prevent future reservations"
                            data-testid="event-change-arrival-blackout-old-timeslot-checkbox"
                            onChange={(event: any) => setBlackoutTimeslot(event.target.checked)}
                        />
                    </FormGroup>
                    <IsGranted permissions={Permission.MODIFY_RESOURCES_GUIDE}>
                        <FormGroup>
                            <Checkbox
                                disabled={isGuideRemovalSelectionDisabled}
                                checked={unassignGuides}
                                label="Remove guide assignment for this event"
                                data-testid="event-change-arrival-remove-guide-assignment-checkbox"
                                onChange={(event: any) => setUnassignGuides(event.target.checked)}
                            />
                        </FormGroup>
                    </IsGranted>
                </Modal.Body>
                <Modal.Footer>
                    <fieldset disabled={isLoadingEvent} className="flex justify-end space-x-4">
                        <NotifyCustomerCheckbox
                            isChecked={notifyCustomer}
                            onChange={(e) => setNotifyCustomer(e.target.checked)}
                        />

                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton
                            isLoading={isLoadingEvent}
                            disabled={selectedPurchases.length === 0 || isTheSameArrival}
                            onClick={handleSubmit}
                        >
                            Change Arrival
                        </SubmitButton>
                    </fieldset>
                </Modal.Footer>
            </>
        );
    }

    if (showCancelModal) {
        return (
            <>
                <Modal.Header>Cancel Operation?</Modal.Header>
                <Modal.Body>
                    <div className="flex flex-col text-center text-gray-darker">
                        <span>
                            Cancelling this operation may result in some of your reservations being changed while others
                            remaining unchanged.
                        </span>
                        <span className="mt-3">Do you still want to cancel this operation?</span>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <SecondaryButton
                        onClick={(e: any) => {
                            setShowCancelModal(false);
                            setSuccessCount(0);
                            handleSubmit(e);
                        }}
                    >
                        Back
                    </SecondaryButton>
                    <Button color="danger" onClick={abortThrottle}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </>
        );
    }

    return (
        <OverlayLoader
            isOpen={isOverlayLoaderOpened}
            color="primary"
            description={
                <>
                    <var data-var="successCount">
                        {successCount} / {selectedPurchases.length}
                    </var>
                    &nbsp;reservations processed
                </>
            }
            showCancelButton={showCancelButton()}
            progress={calculatePercentage(successCount, selectedPurchases.length)}
            onCancel={() => {
                setShowCancelModal(true);
                showCloseIcon(true);
                throttledUpdate.abort();
            }}
            onClose={successCount === selectedPurchases.length ? (e?: any) => onClose(e) : undefined}
        />
    );
};

interface PurchaseListProps {
    onChange: (selected: ExperiencePurchaseItem[]) => void;
    filteredPurchaseItems: ExperiencePurchaseItem[];
    isLoading: boolean;
}

const PurchaseList = ({ onChange, filteredPurchaseItems, isLoading }: PurchaseListProps) => {
    const [selectedPurchase, setSelectedPurchase] = useState<ExperiencePurchaseItem[]>([]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Table>
            <Table.Head>
                <Table.Row>
                    <Table.Header>Name</Table.Header>
                    <Table.Header className="text-center">Group Size</Table.Header>
                    <Table.Header
                        className="text-center underline hover:cursor-pointer"
                        onClick={() => {
                            const selectedItems = selectedPurchase?.length === 0 ? filteredPurchaseItems : [];
                            setSelectedPurchase(selectedItems);
                            onChange(selectedItems);
                        }}
                    >
                        {selectedPurchase?.length === 0 ? "Select All" : "Unselect All"}
                    </Table.Header>
                </Table.Row>
            </Table.Head>

            <Table.Body>
                {filteredPurchaseItems.map((purchaseItem: ExperiencePurchaseItem) => (
                    <Table.Row key={purchaseItem.id} data-testid="event-change-arrival-purchase-list-item">
                        <Table.Cell
                            className="no-translate"
                            data-testid="event-change-arrival-purchase-list-customer-name"
                        >
                            {getCustomerName(purchaseItem.purchase)}
                        </Table.Cell>
                        <Table.Cell
                            className="no-translate text-center"
                            data-testid="event-change-arrival-purchase-list-group-size"
                        >
                            {purchaseItem.quantity}
                        </Table.Cell>
                        <Table.Cell className="flex justify-center">
                            <Checkbox
                                label=""
                                checked={selectedPurchase.some((p: ExperiencePurchaseItem) => purchaseItem.id === p.id)}
                                data-testid="event-change-arrival-purchase-list-purchase-checkbox"
                                onChange={(event_: any) => {
                                    if (
                                        event_.target.checked &&
                                        !selectedPurchase.some((p: ExperiencePurchaseItem) => purchaseItem.id === p.id)
                                    ) {
                                        setSelectedPurchase([...selectedPurchase, purchaseItem]);
                                        onChange([...selectedPurchase, purchaseItem]);
                                        return;
                                    }

                                    setSelectedPurchase([
                                        ...selectedPurchase.filter(
                                            (p: ExperiencePurchaseItem) => p.id !== purchaseItem.id,
                                        ),
                                    ]);
                                    onChange([
                                        ...selectedPurchase.filter(
                                            (p: ExperiencePurchaseItem) => p.id !== purchaseItem.id,
                                        ),
                                    ]);
                                }}
                            />
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
};
